import type { ReactNode } from "react";

/**
 * One stroked icon set, drawn on a 24-unit grid with a 2-unit safe margin so
 * every glyph reads at the same visual size regardless of its shape.
 *
 * The stroke attributes live on the <svg> root and the paths inherit them, so
 * a shape only overrides a value when it genuinely needs to (a dot, a tick).
 * Nothing carries its own colour: everything is currentColor, which is what
 * lets the same icon work in either theme and pick up the semantic status
 * colours (--ok / --warn / --danger) from whatever it sits inside.
 *
 * Inline SVG rather than an icon font or a package: the whole set is smaller
 * than a single font file, needs no external request, and cannot render as a
 * missing-glyph box on a machine that lacks the font — which is exactly what
 * the typographic characters this replaces (◈ ▤ ⏻) were doing.
 */
const PATHS = {
  // ── main navigation ────────────────────────────────────────────
  home: <><path d="M4 10.5 12 4l8 6.5"/><path d="M6 9.6V20h12V9.6"/><path d="M10 20v-5h4v5"/></>,
  lab: <><path d="M9 3h6M10 3v6.2L5.6 17A2 2 0 0 0 7.4 20h9.2a2 2 0 0 0 1.8-3L14 9.2V3"/><path d="M7.6 14h8.8"/></>,
  docs: <><path d="M12 6.5S10 5 6.5 5H4v13h2.5C10 18 12 19.5 12 19.5s2-1.5 5.5-1.5H20V5h-2.5C14 5 12 6.5 12 6.5Z"/><path d="M12 6.5v13"/></>,
  topology: <><circle cx="12" cy="5" r="2.2"/><circle cx="5.5" cy="18.5" r="2.2"/><circle cx="18.5" cy="18.5" r="2.2"/><path d="M12 7.2v4.3m0 0L6.7 16.6M12 11.5l5.3 5.1"/></>,
  tools: <><path d="M14.5 6.2a3.6 3.6 0 0 1 4.9 4.7l-8.4 8.4a2.2 2.2 0 0 1-3.1-3.1l8.3-8.3"/><path d="M9.3 5.5 6.6 8.2 4 7.4l1.9-3 3 .3ZM5.7 8.4l3.4 3.4"/></>,
  monitor: <path d="M3 13h3.4l2.2-6 3.2 12 2.6-8.4L17 13h4"/>,
  instructor: <><rect x="3.5" y="4" width="17" height="11.5" rx="1.8"/><path d="M12 15.5V20M8.5 20h7"/><path d="m7 11.6 2.8-3 2.4 2 2.9-3.4"/></>,

  // ── DNSSEC concepts — the reason not to use a generic icon set ──
  zone: <><circle cx="12" cy="12" r="8.2"/><path d="M3.8 12h16.4M12 3.8c2.2 2.3 3.4 5.2 3.4 8.2s-1.2 5.9-3.4 8.2c-2.2-2.3-3.4-5.2-3.4-8.2S9.8 6.1 12 3.8Z"/></>,
  key: <><circle cx="8" cy="8" r="3.6"/><path d="M10.6 10.6 20 20M16.5 16.5l-2 2M19 14l-2 2"/></>,
  sign: <><path d="M4 17.5c3.4 0 3.4-11 6.8-11s3.4 11 6.8 11"/><path d="M14.5 20H20"/></>,
  chain: <><path d="M10.2 13.8a3.4 3.4 0 0 1 0-4.8l2.4-2.4a3.4 3.4 0 0 1 4.8 4.8l-1 1"/><path d="M13.8 10.2a3.4 3.4 0 0 1 0 4.8l-2.4 2.4a3.4 3.4 0 0 1-4.8-4.8l1-1"/></>,
  server: <><rect x="3.6" y="4.5" width="16.8" height="6" rx="1.6"/><rect x="3.6" y="13.5" width="16.8" height="6" rx="1.6"/><path d="M7 7.5h.01M7 16.5h.01" strokeWidth="2.2"/></>,
  hidden: <><path d="M4 12s3.2-5.5 8-5.5c1.2 0 2.3.3 3.3.9M20 12s-3.2 5.5-8 5.5c-1.3 0-2.4-.4-3.4-1"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="m4.5 4.5 15 15"/></>,
  resolver: <><path d="M4 8h11a4 4 0 0 1 0 8H6"/><path d="m8.6 12.6-2.8 3.4 3.4 2.4"/></>,

  // ── actions ────────────────────────────────────────────────────
  terminal: <><rect x="3.2" y="4.5" width="17.6" height="15" rx="2"/><path d="m7.5 10 2.6 2.4-2.6 2.4M13 15h4"/></>,
  editor: <path d="M9.5 6.5 5 12l4.5 5.5M14.5 6.5 19 12l-4.5 5.5"/>,
  run: <><circle cx="12" cy="12" r="8.2"/><path d="M10.3 9.2l5 2.8-5 2.8V9.2Z"/></>,
  submit: <><path d="M12 16V4.5M12 4.5 7.6 9M12 4.5 16.4 9"/><path d="M4.5 15v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3"/></>,
  reset: <><path d="M19.5 12a7.5 7.5 0 1 1-2.6-5.7"/><path d="M19.8 4.6v4.2h-4.2"/></>,
  power: <><path d="M12 3.8v6.4"/><path d="M7.4 7A7.4 7.4 0 1 0 16.6 7"/></>,
  build: <><path d="M12 3.6 20 8v8l-8 4.4L4 16V8l8-4.4Z"/><path d="M4 8l8 4.4L20 8M12 12.4v8"/></>,
  export: <><path d="M12 4.5V15M12 15l-4.2-4.3M12 15l4.2-4.3"/><path d="M4.6 17.5v.5a2 2 0 0 0 2 2h10.8a2 2 0 0 0 2-2v-.5"/></>,
  login: <><path d="M14.5 5.5H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h7.5"/><path d="M12 12h8M20 12l-3.2-3.2M20 12l-3.2 3.2"/></>,
  copy: <><rect x="9" y="9" width="11" height="11" rx="1.8"/><path d="M15 9V5.8A1.8 1.8 0 0 0 13.2 4H5.8A1.8 1.8 0 0 0 4 5.8v7.4A1.8 1.8 0 0 0 5.8 15H9"/></>,
  groups: <><circle cx="9" cy="9" r="3"/><path d="M3.5 19.5a5.5 5.5 0 0 1 11 0"/><path d="M16 6.4a3 3 0 0 1 0 5.2M17.5 14.6a5.5 5.5 0 0 1 3 4.9"/></>,
  dashboard: <><rect x="3.6" y="3.6" width="7.4" height="7.4" rx="1.5"/><rect x="13" y="3.6" width="7.4" height="7.4" rx="1.5"/><rect x="3.6" y="13" width="7.4" height="7.4" rx="1.5"/><rect x="13" y="13" width="7.4" height="7.4" rx="1.5"/></>,
  slides: <><rect x="3.5" y="5" width="17" height="11" rx="1.8"/><path d="M9 19h6M12 16v3"/></>,

  // ── state and viewport chrome ──────────────────────────────────
  check: <path d="m5.5 12.5 4.2 4.2L18.5 8" strokeWidth="2"/>,
  alert: <><path d="M12 4.8 20.6 19H3.4L12 4.8Z"/><path d="M12 10v4M12 16.6h.01" strokeWidth="1.8"/></>,
  close: <path d="m6.5 6.5 11 11m0-11-11 11" strokeWidth="1.7"/>,
  plus: <path d="M12 5.5v13M5.5 12h13" strokeWidth="1.7"/>,
  "zoom-in": <><circle cx="10.8" cy="10.8" r="6"/><path d="M15.2 15.2 20 20M8.4 10.8h4.8M10.8 8.4v4.8"/></>,
  "zoom-out": <><circle cx="10.8" cy="10.8" r="6"/><path d="M15.2 15.2 20 20M8.4 10.8h4.8"/></>,
  fit: <path d="M9 4.5H5.6a1.6 1.6 0 0 0-1.6 1.6V9M15 4.5h3.4A1.6 1.6 0 0 1 20 6.1V9M9 19.5H5.6A1.6 1.6 0 0 1 4 17.9V15M15 19.5h3.4a1.6 1.6 0 0 0 1.6-1.6V15"/>,
  fullscreen: <path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9M15 4h3.5A1.5 1.5 0 0 1 20 5.5V9M20 15v3.5a1.5 1.5 0 0 1-1.5 1.5H15M9 20H5.5A1.5 1.5 0 0 1 4 18.5V15"/>,
  collapse: <path d="m14 7-5 5 5 5" strokeWidth="1.7"/>,
  expand: <path d="m10 7 5 5-5 5" strokeWidth="1.7"/>,
  "theme-dark": <path d="M19 14.5A7.6 7.6 0 0 1 9.5 5 7.8 7.8 0 1 0 19 14.5Z"/>,
  "theme-light": <><circle cx="12" cy="12" r="4"/><path d="M12 3.4v2.2M12 18.4v2.2M3.4 12h2.2M18.4 12h2.2M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18"/></>,
  logout: <><path d="M14.5 5.5H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h7.5"/><path d="M12 12h8M20 12l-3.2-3.2M20 12l-3.2 3.2"/></>,
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

/**
 * Two sizes only: 16 in navigation and buttons, 18 in card headers and tiles.
 * Other sizes make the fixed 1.6 stroke look inconsistent next to its
 * neighbours, since the stroke scales with the glyph.
 */
export function Icon({ name, size = 16, className }: { name: IconName; size?: 16 | 18; className?: string }) {
  return (
    <svg
      className={className ? `icon ${className}` : "icon"}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative by default: these always sit beside a real text label, and
      // the icon-only controls carry their meaning in the button's aria-label.
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
